import { WORKSPACE_ORG_ADMIN } from '../../../constants/roles';
import type { WorkspaceTeamMember as WorkspaceMember } from '../../Team/utils/workspaceTeam';

import { decodeApproverRoleValue } from './approvalRulesDisplay';
import { ORG_SCOPED_RESOURCE_TYPE } from './constants';

const WORKSPACE_MEMBER_ROLE_BY_APPROVER_ROLE: Readonly<Record<string, string>> = {
  admin: WORKSPACE_ORG_ADMIN,
};

type CountEligibleApproversArgs = {
  approvers: string[];
  resourceType: string;
  targetEntityId: string;
};

function buildMembersByLegalEntity(
  members: WorkspaceMember[],
): ReadonlyMap<string, ReadonlySet<WorkspaceMember>> {
  const membersByLegalEntity = new Map<string, Set<WorkspaceMember>>();

  for (const member of members) {
    for (const { legalEntityId } of member.effectiveRoles) {
      const legalEntityMembers = membersByLegalEntity.get(legalEntityId) ?? new Set();
      legalEntityMembers.add(member);
      membersByLegalEntity.set(legalEntityId, legalEntityMembers);
    }
  }

  return membersByLegalEntity;
}

function getOrgMemberRole(approver: string): string {
  const { productId, role } = decodeApproverRoleValue(approver);
  if (productId === 'workspace') {
    return WORKSPACE_MEMBER_ROLE_BY_APPROVER_ROLE[role] ?? role;
  }

  return role;
}

function countOrgApprovers(members: WorkspaceMember[], approvers: string[]): number {
  const selectedMemberRoles = new Set(approvers.map(getOrgMemberRole));

  return members.filter((member) => member.orgRoles.some((role) => selectedMemberRoles.has(role)))
    .length;
}

function countLegalEntityApprovers(
  members: Iterable<WorkspaceMember>,
  approvers: string[],
  legalEntityId: string,
): number {
  const approverRoles = new Set(approvers);

  return [...members].filter((member) =>
    member.effectiveRoles.some(
      (entityRole) =>
        entityRole.legalEntityId === legalEntityId && approverRoles.has(entityRole.role),
    ),
  ).length;
}

export function createApproverCounter(members: WorkspaceMember[] | undefined) {
  const membersByLegalEntity = buildMembersByLegalEntity(members ?? []);

  return ({
    approvers,
    resourceType,
    targetEntityId,
  }: CountEligibleApproversArgs): number | undefined => {
    if (!members) {
      return undefined;
    }

    if (approvers.length === 0) {
      return 0;
    }

    if (resourceType === ORG_SCOPED_RESOURCE_TYPE) {
      return countOrgApprovers(members, approvers);
    }

    if (!targetEntityId) {
      return 0;
    }

    return countLegalEntityApprovers(
      membersByLegalEntity.get(targetEntityId) ?? [],
      approvers,
      targetEntityId,
    );
  };
}
